// Contact.js
import React, { useState } from 'react';
import './Pages/PagesCss/Contact.css';
import { useNavigate } from 'react-router-dom';

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icons = {
  Mail: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...p}>
      <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M2 8L12 14L22 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Docs: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M14 2V8H20M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Billing: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...p}>
      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M2 10H22" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M6 15H10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Feature: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    </svg>
  ),
  Send: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Check: (p) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Clock: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...p}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  ArrowRight: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  ChevronDown: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  GetStarted: (p) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...p}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M10 8.5L16 12L10 15.5V8.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
),
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const TOPICS = [
  { value: '',          label: 'Select a topic…' },
  { value: 'bug',       label: 'Report a bug' },
  { value: 'billing',   label: 'Billing & payments' },
  { value: 'feature',   label: 'Feature request' },
  { value: 'account',   label: 'Account & login' },
  { value: 'tool',      label: 'Tool not working' },
  { value: 'other',     label: 'Other' },
];

const FAQS = [
  {
    q: 'Why is my PDF failing to process?',
    a: 'The most common causes are: file exceeding the size limit for your plan (10 MB free, 100 MB Pro), a password-protected file, or a corrupted PDF. Try the Repair PDF tool first, then re-upload.',
  },
  {
    q: 'Are my files kept private?',
    a: 'Yes. All files are processed in-memory and permanently deleted from our servers within 1 hour of processing. We never read, store long-term, or share your documents with any third party.',
  },
  {
    q: 'Why does my converted Word file look different?',
    a: 'Complex layouts with custom fonts, tables, or images may not convert pixel-perfectly. For best results, ensure your PDF was originally created from a Word document rather than a scanned image.',
  },
  {
    q: 'How do I cancel my Pro or Business subscription?',
    a: 'Go to Account → Billing → Cancel plan. You keep full access until the end of your billing period. No cancellation fees, and you can resubscribe at any time.',
  },
  {
    q: 'Can I process files in bulk?',
    a: 'Batch processing is available on Pro and Business plans. On the Free plan, files must be uploaded one at a time with a 2 files/day limit.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required';
    if (!form.email.trim())   e.email   = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.topic)          e.topic   = 'Please select a topic';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.trim().length < 20) e.message = 'Please give us a bit more detail (20+ chars)';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1400);
  };

  const field = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  };

  // ── Success state ──────────────────────────────────────────
  if (submitted) {
    return (
      <div className="ct-page">
        <div className="ct-success-wrap">
          <div className="ct-success-card">
            <div className="ct-success-icon">
              <Icons.Check />
            </div>
            <h2 className="ct-success-title">Message sent!</h2>
            <p className="ct-success-body">
              Thanks <strong>{form.name.split(' ')[0]}</strong> we've received your message and will reply to <strong>{form.email}</strong> within 24 hours.
            </p>
            <div className="ct-success-btns">
              <button className="ct-btn ct-btn--primary" onClick={() => { setSubmitted(false); setForm({ name: '', email: '', topic: '', message: '' }); }}>
                Send another
              </button>
              <button className="ct-btn ct-btn--ghost" onClick={() => navigate('/')}>
                Back to home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ct-page">

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="ct-hero">
        <div className="ct-hero-bg" aria-hidden="true" />
        <div className="ct-container">
          <div className="ct-hero-badge">
            <span className="ct-badge-dot" />
            We're here to help
          </div>
          <h1 className="ct-hero-title">
            Get in touch with<br />
            <span className="ct-highlight">our team</span>
          </h1>
          <p className="ct-hero-sub">
            Have a question, spotted a bug, or want to suggest a feature?
            Pick the fastest channel below or fill in the form.
          </p>
        </div>
      </section>

      {/* ── Main: form + side info ─────────────────────────── */}
      <section className="ct-main-section">
        <div className="ct-container">
          <div className="ct-main-grid">

            {/* Contact form */}
            <div className="ct-form-col">
              <div className="ct-form-card">
                <div className="ct-form-header">
                  <h2 className="ct-form-title">Send us a message</h2>
                  <p className="ct-form-sub">We read every message and reply within 24 hours.</p>
                </div>

                <form className="ct-form" onSubmit={handleSubmit} noValidate>

                  {/* Name + Email row */}
                  <div className="ct-field-row">
                    <div className="ct-field">
                      <label className="ct-label">Full name</label>
                      <input
                        className={`ct-input${errors.name ? ' ct-input--err' : ''}`}
                        type="text"
                        placeholder="Alex Johnson"
                        value={form.name}
                        onChange={e => field('name', e.target.value)}
                      />
                      {errors.name && <span className="ct-err">{errors.name}</span>}
                    </div>
                    <div className="ct-field">
                      <label className="ct-label">Email address</label>
                      <input
                        className={`ct-input${errors.email ? ' ct-input--err' : ''}`}
                        type="email"
                        placeholder="alex@email.com"
                        value={form.email}
                        onChange={e => field('email', e.target.value)}
                      />
                      {errors.email && <span className="ct-err">{errors.email}</span>}
                    </div>
                  </div>

                  {/* Topic select */}
                  <div className="ct-field">
                    <label className="ct-label">Topic</label>
                    <div className="ct-select-wrap">
                      <select
                        className={`ct-select${errors.topic ? ' ct-input--err' : ''}`}
                        value={form.topic}
                        onChange={e => field('topic', e.target.value)}
                      >
                        {TOPICS.map(t => (
                          <option key={t.value} value={t.value} disabled={!t.value}>{t.label}</option>
                        ))}
                      </select>
                      <Icons.ChevronDown className="ct-select-arrow" />
                    </div>
                    {errors.topic && <span className="ct-err">{errors.topic}</span>}
                  </div>

                  {/* Message */}
                  <div className="ct-field">
                    <label className="ct-label">
                      Message
                      <span className="ct-char-count">{form.message.length} / 1000</span>
                    </label>
                    <textarea
                      className={`ct-textarea${errors.message ? ' ct-input--err' : ''}`}
                      placeholder="Describe your issue or question in detail. The more context you give us, the faster we can help."
                      rows={6}
                      maxLength={1000}
                      value={form.message}
                      onChange={e => field('message', e.target.value)}
                    />
                    {errors.message && <span className="ct-err">{errors.message}</span>}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className={`ct-submit${loading ? ' loading' : ''}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="ct-spinner" />
                    ) : (
                      <>
                        <Icons.Send /> Send message
                      </>
                    )}
                  </button>

                  <p className="ct-privacy">
                    By sending a message you agree to our{' '}
                    <a href="/privacy">Privacy Policy</a>.
                    We never share your information.
                  </p>
                </form>
              </div>
            </div>

            {/* Side info */}
            <div className="ct-side-col">

              {/* Response time card */}
              <div className="ct-info-card">
                <h3 className="ct-info-title">Response times</h3>
                <div className="ct-response-list">
                  {[
                    { plan: 'Free',     time: 'Within 48 hours',  color: '#64748B' },
                    { plan: 'Pro',      time: 'Within 24 hours',  color: '#3B82F6' },
                    { plan: 'Business', time: 'Within 4 hours',   color: '#10B981' },
                  ].map((r, i) => (
                    <div key={i} className="ct-response-row">
                      <span className="ct-response-plan" style={{ color: r.color, background: `${r.color}12` }}>
                        {r.plan}
                      </span>
                      <span className="ct-response-time">
                        <Icons.Clock /> {r.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick links */}
              <div className="ct-info-card">
                <h3 className="ct-info-title">Quick links</h3>
                <div className="ct-quick-links">
                  {[
                    { icon: Icons.Docs,    label: 'Explore All Tools',      href: '/PDFTools'    },
                    { icon: Icons.Billing,     label: 'Known Pricing Plan',     href: '/Pricing'  },
                    { icon: Icons.Feature, label: 'About QuickPDFs.net',        href: '/AboutUs'   },
                    { icon: Icons.GetStarted, label: 'Try Our PDF Tools Now',   href: '/PDFMerger' },
                  ].map((l, i) => (
                    <a key={i} href={l.href} className="ct-quick-link">
                      <span className="ct-quick-icon"><l.icon /></span>
                      <span className="ct-quick-label">{l.label}</span>
                      <Icons.ArrowRight className="ct-quick-arrow" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section className="ct-faq-section">
        <div className="ct-container ct-container--narrow">
          <div className="ct-faq-head">
            <span className="ct-section-label">Common questions</span>
            <h2 className="ct-section-title">Before you write in</h2>
            <p className="ct-section-sub">Most questions are answered here saves you waiting for a reply.</p>
          </div>

          <div className="ct-faqs">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className={`ct-faq${openFaq === i ? ' open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="ct-faq-q">
                  {faq.q}
                  <span className="ct-faq-toggle">{openFaq === i ? '−' : '+'}</span>
                </div>
                {openFaq === i && <div className="ct-faq-a">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}